import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, } from 'react';
import { Portal } from '../../../portal';
import { NavigationContext } from '../../provider';
import { NavigationFlyoutContainer, NavigationFlyoutContent, NavigationFlyoutHeader, NavigationFlyoutInner, NavigationFlyoutShowAll, NavigationFlyoutTitle, } from './styles/navigation-flyout.styled';
export const NavigationFlyout = forwardRef(({ title, showAll, children, reference, onClose, dataTest = 'navigation-sub-items-flyout', }, ref) => {
    const innerRef = useRef(null);
    const { isKeyboardNavigating, focusedNavigationElement, activeItemReference, setActiveItemReference, navigationViewportBreakpoint, } = useContext(NavigationContext);
    const wasActive = useRef(reference === activeItemReference);
    const isActive = reference === activeItemReference;
    const handleMouseEnter = () => {
        if (['lg', 'xl', 'xxl'].includes(navigationViewportBreakpoint)) {
            setActiveItemReference(reference);
        }
    };
    const handleMouseLeave = () => {
        if (['lg', 'xl', 'xxl'].includes(navigationViewportBreakpoint)) {
            setActiveItemReference(null);
        }
    };
    useImperativeHandle(ref, () => innerRef.current, []);
    useEffect(() => {
        const element = innerRef.current;
        let firstAnchor;
        let lastAnchor;
        const handleKeyDownFirstAnchor = (e) => {
            if (e.key === 'Tab' && e.shiftKey) {
                e.preventDefault();
                // the user wants to go back to the main menu items
                setActiveItemReference(null);
                focusedNavigationElement === null || focusedNavigationElement === void 0 ? void 0 : focusedNavigationElement.focus();
            }
        };
        const handleKeyDownLastAnchor = (e) => {
            if (e.key === 'Tab' && !e.shiftKey) {
                e.preventDefault();
                // the user wants to go back to the main menu items
                setActiveItemReference(null);
                focusedNavigationElement === null || focusedNavigationElement === void 0 ? void 0 : focusedNavigationElement.focus();
            }
        };
        if (element !== null && isKeyboardNavigating) {
            if (isActive) {
                const anchors = Array.from(element.querySelectorAll('a'));
                firstAnchor = anchors.shift();
                lastAnchor = anchors.pop();
                if (firstAnchor) {
                    firstAnchor.focus();
                    firstAnchor.addEventListener('keydown', handleKeyDownFirstAnchor);
                }
                if (lastAnchor && lastAnchor !== firstAnchor) {
                    lastAnchor.addEventListener('keydown', handleKeyDownLastAnchor);
                }
            }
            else if (wasActive.current) {
                focusedNavigationElement === null || focusedNavigationElement === void 0 ? void 0 : focusedNavigationElement.focus();
            }
        }
        wasActive.current = isActive;
        return () => {
            if (firstAnchor) {
                firstAnchor.removeEventListener('keydown', handleKeyDownFirstAnchor);
            }
            if (lastAnchor && lastAnchor !== firstAnchor) {
                lastAnchor.removeEventListener('keydown', handleKeyDownLastAnchor);
            }
        };
    }, [focusedNavigationElement, isActive, isKeyboardNavigating, setActiveItemReference]);
    const flyoutComponent = (_jsx(NavigationFlyoutContainer, { ref: innerRef, "data-test": dataTest, "$isActive": isActive, onClick: (e) => {
            setActiveItemReference(null);
            if (onClose) {
                onClose(e);
            }
        }, children: _jsx(NavigationFlyoutInner, { onMouseLeave: handleMouseLeave, onMouseEnter: handleMouseEnter, onClick: (e) => {
                e.stopPropagation();
            }, children: _jsxs(NavigationFlyoutContent, { children: [_jsxs(NavigationFlyoutHeader, { children: [_jsx(NavigationFlyoutTitle, { tagName: "a", href: showAll.href, children: title }), _jsx(NavigationFlyoutShowAll, Object.assign({ href: showAll.href }, (showAll.onClick && {
                                onClick: showAll.onClick,
                            }), { children: showAll.label }))] }), children] }) }) }));
    if (['xxs', 'xs', 'sm', 'md'].includes(navigationViewportBreakpoint)) {
        return flyoutComponent;
    }
    return (_jsx(Portal, { id: `navigation-flyout-${reference}`, cleanup: true, children: flyoutComponent }));
});
NavigationFlyout.displayName = 'NavigationFlyout';
